// pages/login.js
import { useState, useEffect } from "react";
import { signIn } from "next-auth/react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import env from "../../config/env";
import Loader from "../../components/agreementGeneration/Loader";

export default function LoginOrSignup({ isSignup = false }) {
  const router = useRouter();

  const { data: session, status } = useSession();
  const [fullName, setFullName]     = useState("");
  const [email, setEmail]           = useState("");
  const [code, setCode]             = useState("");
  const [step, setStep]             = useState(1);   // 1 = send OTP, 2 = verify
  const [loading, setLoading]       = useState(false);

  const [fullNameError, setFullNameError] = useState("");
  const [emailError, setEmailError]       = useState("");
  const [codeError, setCodeError]         = useState("");
  const [generalError, setGeneralError]   = useState("");
  const [loggingInLoading, setLoggingInLoading] = useState(false)

  // redirect if already logged in
  useEffect(() => {
    if (status === "loading") return;
    const token = typeof window !== "undefined"
      ? localStorage.getItem("token")
      : null;
    if (session || token) {
      router.replace("/dashboard/transactions");
    }
  }, [session, status, router]);

  // Google login → middleware redirects here with ?reason=archived when the account
  // has been archived by an admin. Show the same copy the backend surfaces on the
  // OTP path so the message is consistent across sign-in modes.
  useEffect(() => {
    if (!router.isReady) return;
    if (router.query.reason === "archived") {
      setGeneralError("Your account is no longer active. Reach out to your organisation's admin.");
    }
  }, [router.isReady, router.query.reason]);

  function validateEmail(e) {
    const re = /^\S+@\S+\.\S+$/;
    return re.test(e);
  }

  // STEP 1: send OTP
  async function sendCode() {
    // clear errors
    setFullNameError("");
    setEmailError("");
    setGeneralError("");

    if (isSignup && !fullName.trim()) {
      setFullNameError("Full name is required.");
      return;
    }
    if (!email.trim()) {
      setEmailError("Email is required.");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase(), accountId: env.accountId })
      });
      if(!res.ok){
        // Read the body ONCE so we can branch on the archive discriminator without
        // consuming the stream twice.
        const body = await res.json().catch(() => ({}));
        if (res.status === 403 && body?.data?.reason === "archived") {
          setGeneralError(body?.message || "Your account is no longer active. Reach out to your organisation's admin.");
        } else if(res.status==401){
          // Not a published user (never invited, or invited-but-unverified).
          // Access is invite-only — the org admin sends an invitation.
          setGeneralError("You don't have access — please ask your organization's admin to send you an invitation.");
        }else{
           throw new Error();
        }
      }else{
        setStep(2);
      }
    } catch {
      setGeneralError("We couldn't send the code. Please check the email address and try again.");
    } finally {
      setLoading(false);
    }
  }

  // STEP 2: verify OTP
  async function verifyCode() {
    // clear errors
    setCodeError("");
    setGeneralError("");

    if (!code.trim()) {
      setCodeError("Please enter the code.");
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setCodeError("The code must be a 6-digit number.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.toLowerCase(),
          otp: code,
          accountId: env.accountId,
          ...(isSignup ? { fullName } : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 403 && body?.data?.reason === "archived") {
          setGeneralError(body?.message || "Your account is no longer active. Reach out to your organisation's admin.");
          return;
        }
        throw new Error();
      }
      await res.json();
      setLoggingInLoading(true)
      // Fresh-login moment: multi-org accounts pick their workspace before
      // landing (org chooser interstitial). Best-effort — any failure on
      // the membership fetch lands on the dashboard as before.
      let target = "/dashboard/transactions";
      try {
        // Bounded: a hung backend must not stall login — before this
        // timeout existed, this await ran BEFORE navigation and before the
        // 5s safety-net timer was even armed.
        const orgsRes = await fetch("/api/auth/organizations", { signal: AbortSignal.timeout(2500) });
        if (orgsRes.ok) {
          const orgsBody = await orgsRes.json();
          const list = (orgsBody.data && orgsBody.data.organizations) || [];
          if (list.length > 1) target = "/dashboard/choose-organization?from=login";
        }
      } catch {
        /* land normally */
      }
      router.replace(target);

      // Safety net: if client-side navigation hasn't completed in 5 seconds, force a full page load
      setTimeout(() => {
        window.location.href = target;
      }, 5000);
    } catch {
      setGeneralError("Verification failed. Please check the code and try again.");
    } finally {
      setLoading(false);
    }
  }

  const sendLabel   = loading && step === 1 ? "Sending…"   : isSignup ? "Sign Up"     : "Sign In";
  const verifyLabel = loading && step === 2 ? "Verifying…" : "Verify";
  // hide form while session/token loading
  if (loggingInLoading) {
    return <Loader loadingMessage="Logging you in…" />;
  }
  return (
    <div className="min-h-screen bg-tertiary-99 flex items-center justify-center p-4">
      <div className="bg-lightSurfaceContainer p-8 rounded-2xl shadow-custom1 w-full max-w-sm">
        <div className="mb-6 text-center">
          <img src="/assets/rebillion_logo.svg" className="mx-auto h-10" />
          <h1 className="mt-4 text-xl font-semibold text-onSurface">
            {isSignup ? "Create your account" : "Welcome back!"}
          </h1>
          <p className="text-sm text-onSurfaceVariant mt-1">
            {isSignup
              ? "Sign up with your email or Google"
              : "Sign in with your email or Google"}
          </p>
        </div>

        {/* full-form error */}
        {generalError && (
          <p className="mb-4 text-errorColorRed text-sm">{generalError}</p>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <>
            {isSignup && (
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Full Name"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  className="
                    w-full p-3
                    border border-neutralVariant-80
                    rounded-xl bg-white
                    text-onSurface placeholder-onSurfaceVariant
                    focus:outline-none focus:ring-0
                  "
                />
                {fullNameError && (
                  <p className="mt-1 text-errorColorRed text-sm">
                    {fullNameError}
                  </p>
                )}
              </div>
            )}
            <div className="mb-4">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="
                  w-full p-3
                  border border-neutralVariant-80
                  rounded-xl bg-white
                  text-onSurface placeholder-onSurfaceVariant
                  focus:outline-none focus:ring-0
                "
              />
              {emailError && (
                <p className="mt-1 text-errorColorRed text-sm">
                  {emailError}
                </p>
              )}
            </div>
          </>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <>
            <div className="mb-4">
              <input
                type="email"
                value={email}
                disabled
                className="
                  w-full p-3
                  bg-lightSurfaceVariant rounded-xl
                  text-onSurfaceVariant placeholder-onSurfaceVariant
                  cursor-not-allowed focus:outline-none focus:ring-0
                "
              />
            </div>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Enter code"
                value={code}
                onChange={e => setCode(e.target.value)}
                className="
                  w-full p-3
                  border border-neutralVariant-80
                  rounded-xl bg-white
                  text-onSurface placeholder-onSurfaceVariant
                  focus:outline-none focus:ring-0
                "
              />
              {codeError && (
                <p className="mt-1 text-errorColorRed text-sm">
                  {codeError}
                </p>
              )}
            </div>
          </>
        )}

        <button
          onClick={step === 1 ? sendCode : verifyCode}
          disabled={loading}
          className="
            mt-6 w-full p-3 rounded-2xl
            bg-primary-50 text-lightOnPrimary
            disabled:opacity-50
          "
        >
          {step === 1 ? sendLabel : verifyLabel}
        </button>

        <div className="mt-6 flex justify-center">
          <button
            onClick={() =>
              signIn("google", {
                prompt:      "select_account",
                callbackUrl: `${window.location.origin}/dashboard/transactions`,
              })
            }
            className="
              flex items-center space-x-2 p-3
              border border-neutralVariant-80
              rounded-xl
            "
          >
            <img
              src="https://www.svgrepo.com/show/355037/google.svg"
              className="h-5 w-5"
            />
            <span className="text-onSurface">
              {isSignup ? "Sign up with Google" : "Sign in with Google"}
            </span>
          </button>
        </div>

        {/* Legal subtext — mirrors Canva's login footer. Links reuse the
            marketing-site URLs already used by components/landingPage/footer.js. */}
        <p className="mt-6 text-center text-xs leading-relaxed text-onSurfaceVariant">
          By continuing, you agree to Rebillion&apos;s{" "}
          <a
            href="https://rebillion.ai/terms-of-service/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-50 underline hover:opacity-80"
          >
            Terms of Use
          </a>
          . Read our{" "}
          <a
            href="https://rebillion.ai/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-50 underline hover:opacity-80"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
